// ─── pages/BoardView.jsx ─────────────────────────────────────────────────────
import { useState, useEffect, useRef, useMemo } from "react";
import config from "../config";
import Swal from "sweetalert2";
import { checklistProgress } from "../store.js";
import { LabelPill, ChecklistBar, AddCardInline, CardModal } from "../Create_plan_Trello/Shared";

// ─── Themes ───────────────────────────────────────────────────────────────────
const THEMES = {
  dark: {
    name: "Dark", icon: "🌙",
    bg: "#0a1628", colBg: "#0f1e30", colHeader: "#0b1826",
    surface: "#152438", surfaceHov: "#1c2f45",
    border: "#1a3050", borderHov: "#2d5a8a",
    accent: "#579dff", text: "#dce8f5",
    textMid: "#8aaccc", textMuted: "#4a6a8a",
    textDim: "#6b8caf", red: "#f87168",
    cardBg: "#1a2f45", cardHov: "#213649",
  },
  light: {
    name: "Light", icon: "☀️",
    bg: "#f0f4f8", colBg: "#ffffff", colHeader: "#f7f9fc",
    surface: "#eef2f7", surfaceHov: "#e4eaf2",
    border: "#d0dae8", borderHov: "#94afd0",
    accent: "#2563eb", text: "#1e3a5f",
    textMid: "#4a6a8a", textMuted: "#8aaccc",
    textDim: "#6b8caf", red: "#dc2626",
    cardBg: "#ffffff", cardHov: "#f0f4f8",
  },
  midnight: {
    name: "Midnight", icon: "🌃",
    bg: "#0d0d1a", colBg: "#13132b", colHeader: "#0d0d1f",
    surface: "#1a1a35", surfaceHov: "#22224a",
    border: "#2a2a55", borderHov: "#4a4aaa",
    accent: "#a78bfa", text: "#e8e4ff",
    textMid: "#9d97cc", textMuted: "#5a5580",
    textDim: "#7a74aa", red: "#f87168",
    cardBg: "#1e1e3d", cardHov: "#252550",
  },
  forest: {
    name: "Forest", icon: "🌿",
    bg: "#0a1a10", colBg: "#0e1f14", colHeader: "#091710",
    surface: "#132019", surfaceHov: "#1a2e22",
    border: "#1e3a28", borderHov: "#2d6040",
    accent: "#4bce97", text: "#d0f0dc",
    textMid: "#7ab894", textMuted: "#3d6b52",
    textDim: "#5a9470", red: "#f87168",
    cardBg: "#172518", cardHov: "#1e3022",
  },
};

const COLOR_PALETTE = [
  "#579dff", "#f5a623", "#a78bfa", "#4bce97",
  "#f87168", "#faa53d", "#e879f9", "#34d399",
  "#fb923c", "#f43f5e", "#38bdf8", "#facc15",
];

const BG_PRESETS = [
  { label: "Default", value: null },
  { label: "Ocean", value: "linear-gradient(135deg,#0a1628 0%,#0d2744 50%,#0a1628 100%)" },
  { label: "Sunset", value: "linear-gradient(135deg,#1a0a2e 0%,#2d0a1a 50%,#0a1210 100%)" },
  { label: "Aurora", value: "linear-gradient(135deg,#0a2818 0%,#0a1a28 40%,#1a0a28 100%)" },
  { label: "Dusk", value: "linear-gradient(135deg,#1a1a0a 0%,#2a1a0a 50%,#0a1a2a 100%)" },
  { label: "Deep", value: "linear-gradient(135deg,#050510 0%,#0d0520 60%,#050d10 100%)" },
];

const CARD_SIZES = {
  compact: { name: "Compact", width: 240, icon: "▤" },
  normal: { name: "Normal", width: 300, icon: "▦" },
  wide: { name: "Wide", width: 360, icon: "▧" },
};
const STATUSES = [
  { key: "todo", label: "Open", color: "#579dff", emoji: "📋" },
  { key: "pending", label: "Pending", color: "#f87168", emoji: "⏳" },
  { key: "inprogress", label: "In Progress", color: "#faa53d", emoji: "⚙️" },
  { key: "complete", label: "Complete", color: "#4bce97", emoji: "✅" },
];
const STATUS_MAP = Object.fromEntries(STATUSES.map(s => [s.key, s]));

const SORT_OPTIONS = [
  { key: "default", label: "Default", icon: "↕️" },
  { key: "alpha_asc", label: "A → Z", icon: "🔤" },
  { key: "alpha_desc", label: "Z → A", icon: "🔡" },
  { key: "newest", label: "Newest", icon: "🕐" },
  { key: "oldest", label: "Oldest", icon: "🕛" },
];
const FILTER_OPTIONS = [
  { key: "all", label: "All", icon: "🗂️", color: null },
  { key: "todo", label: "Open", icon: "📋", color: "#579dff" },
  { key: "pending", label: "Pending", icon: "⏳", color: "#f87168" },
  { key: "inprogress", label: "In Progress", icon: "⚙️", color: "#faa53d" },
  { key: "complete", label: "Complete", icon: "✅", color: "#4bce97" },
];

// ─── Board prefs (localStorage) ───────────────────────────────────────────────
const PREF_KEY = "boardPrefs_v1";
const loadPrefs = () => { try { return JSON.parse(localStorage.getItem(PREF_KEY) || "{}"); } catch { return {}; } };
const savePrefs = (p) => localStorage.setItem(PREF_KEY, JSON.stringify(p));
const getColorOverrides = () => { try { return JSON.parse(localStorage.getItem("boardColors") || "{}"); } catch { return {}; } };
const saveColorOverride = (id, color) => { const o = getColorOverrides(); o[id] = color; localStorage.setItem("boardColors", JSON.stringify(o)); };
const resolveColor = (id, def) => getColorOverrides()[id] || def;

// ─── getUser ──────────────────────────────────────────────────────────────────
const getUser = () => {
  try {
    const raw = localStorage.getItem("admin");
    if (raw) { const p = JSON.parse(raw); return { username: p.username || "system", fullNames: p.fullNames || p.username || "System", role: (p.role || "admin").toLowerCase() }; }
    const raw2 = localStorage.getItem("user");
    if (raw2) { const p = JSON.parse(raw2); return { username: p.username || "user", fullNames: p.fullNames || p.username || "User", role: "user" }; }
  } catch { }
  return { username: "system", fullNames: "System", role: "admin" };
};

// ─── Serialization ────────────────────────────────────────────────────────────
const toApiCard = (card) => ({
  id: card.id ?? 0,
  colId: card.colId,
  title: card.title?.trim() || "",
  description: card.desc ?? card.description ?? "",
  labels: Array.isArray(card.labels) ? card.labels.join(",") : (card.labels ?? ""),
  checklist: Array.isArray(card.checklist) ? JSON.stringify(card.checklist) : (card.checklist ?? "[]"),
  archived: card.archived ?? false,
  status: card.status ?? "todo",
  createdBy: card.createdBy || getUser().username,
  createdAt: card.createdAt ? (typeof card.createdAt === "number" ? new Date(card.createdAt).toISOString() : card.createdAt) : new Date().toISOString(),
  ticketAllowed: card.ticketAllowed ?? false,
  ticketRef: card.ticketRef || null,
});

const fromApiCard = (card) => ({
  ...card,
  desc: card.description ?? "",
  labels: Array.isArray(card.labels) ? card.labels : (card.labels ? card.labels.split(",").map(s => s.trim()).filter(Boolean) : []),
  checklist: (() => { if (Array.isArray(card.checklist)) return card.checklist; try { return JSON.parse(card.checklist || "[]"); } catch { return []; } })(),
  status: card.status ?? "todo",
  attachments: card.attachments ?? [],
  ticketAllowed: card.ticketAllowed ?? false,
  ticketRef: card.ticketRef || null,
});
// ─── Swal ─────────────────────────────────────────────────────────────────────
const swalBase = { background: "#1e293b", color: "#f1f5f9", confirmButtonColor: "#579dff", cancelButtonColor: "#334155", customClass: { popup: "swb-popup" } };
const swalPrompt = async (title, placeholder = "") => {
  const { value, isConfirmed } = await Swal.fire({ ...swalBase, title, input: "text", inputPlaceholder: placeholder, inputAttributes: { autocomplete: "off" }, showCancelButton: true, confirmButtonText: "Confirm", cancelButtonText: "Cancel", inputValidator: v => !v?.trim() && "Required!", customClass: { ...swalBase.customClass, input: "swb-input" } });
  return isConfirmed ? value?.trim() : null;
};
const swalConfirm = async (title, text = "", confirmText = "Delete") =>
  (await Swal.fire({ ...swalBase, title, text, icon: "warning", showCancelButton: true, confirmButtonText: confirmText, confirmButtonColor: "#ef4444" })).isConfirmed;
const swalError = (text) => Swal.fire({ ...swalBase, icon: "error", title: "Error", text });

const fmtSize = (b) => b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`;
const isImage = (ct) => ct?.startsWith("image/");

// ─── applySort ────────────────────────────────────────────────────────────────
function applySort(cards, sortKey) {
  if (sortKey === "default") return cards;
  return [...cards].sort((a, b) => {
    if (sortKey === "alpha_asc") return a.title.localeCompare(b.title);
    if (sortKey === "alpha_desc") return b.title.localeCompare(a.title);
    const da = new Date(a.createdAt || 0).getTime();
    const db = new Date(b.createdAt || 0).getTime();
    if (sortKey === "newest") return db - da;
    if (sortKey === "oldest") return da - db;
    return 0;
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// ─── CreateTicketSection ─────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
const PRIORITY_COLORS = {
  Critical: { bg: "#f87168", border: "#f8716888", text: "#f87168", dim: "#f8716822" },
  High: { bg: "#fb923c", border: "#fb923c88", text: "#fb923c", dim: "#fb923c22" },
  Medium: { bg: "#fbbf24", border: "#fbbf2488", text: "#fbbf24", dim: "#fbbf2422" },
  Low: { bg: "#34d399", border: "#34d39988", text: "#34d399", dim: "#34d39922" },
};

const STATUS_TO_PRIORITY = { todo: "Medium", inprogress: "High", pending: "High", complete: "Low" };

const suggestDeadline = (p) => {
  const d = new Date();
  if (p === "Critical") d.setHours(d.getHours() + 4);
  else if (p === "High") d.setDate(d.getDate() + 1);
  else if (p === "Medium") d.setDate(d.getDate() + 3);
  else d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 16);
};

const generateTicketNumber = () => {
  const now = new Date();
  const rand = Math.floor(Math.random() * 9000) + 1000;
  return `TKT-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}-${rand}`;
};

function TicketFieldRow({ icon, label, value }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 7, fontSize: 11 }}>
      <span style={{ flexShrink: 0 }}>{icon}</span>
      <span style={{ color: "#4a6a8a", flexShrink: 0, minWidth: 68 }}>{label}:</span>
      <span style={{ color: "#8aaccc", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{value || "—"}</span>
    </div>
  );
}
function CreateTicketSection({ card, onTicketCreated, C }) {
  const currentUser = getUser();
  const isSuperAdmin = currentUser.role === "superadmin";
  const isAllowed = card?.ticketAllowed === true;

  // ── ALL HOOKS MUST BE AT THE TOP — before any early return ──
  const initTicket = card?.ticketRef || null;
  const [enabled, setEnabled] = useState(!!initTicket);
  const [expanded, setExpanded] = useState(false);
  const [creating, setCreating] = useState(false);
  const [existingTicket, setExistingTicket] = useState(initTicket);
  const [priority, setPriority] = useState(STATUS_TO_PRIORITY[card?.status] || "Medium");
  const [deadline, setDeadline] = useState("");

  useEffect(() => {
    if (card?.ticketRef && card.ticketRef !== existingTicket) {
      setExistingTicket(card.ticketRef);
      setEnabled(true);
    }
  }, [card?.ticketRef]);

  // ── EARLY RETURNS after hooks ──────────────────────────────────────────────

  // Locked for non-superadmin
  if (!isAllowed && !isSuperAdmin) {
    return (
      <div style={{
        marginTop: 14, borderRadius: 12,
        border: "1px solid #1a3050",
        background: "linear-gradient(135deg, #0a1628 0%, #0d1f35 100%)",
        padding: "14px 16px",
        display: "flex", alignItems: "center", gap: 12,
        position: "relative", overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", right: -20, top: -20,
          width: 80, height: 80, borderRadius: "50%",
          background: "radial-gradient(circle, #1a305080 0%, transparent 70%)",
          pointerEvents: "none",
        }} />
        <div style={{
          width: 36, height: 36, borderRadius: 10, flexShrink: 0,
          background: "#1a3050", border: "1px solid #253d5e",
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
        }}>🔒</div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#4a6a8a", letterSpacing: ".04em" }}>
            TICKET CREATION LOCKED
          </div>
          <div style={{ fontSize: 10, color: "#2d4a6a", marginTop: 3, lineHeight: 1.4 }}>
            A superadmin must enable this for the card
          </div>
        </div>
      </div>
    );
  }

  // Superadmin sees notice when disabled
  if (!isAllowed && isSuperAdmin) {
    return (
      <div style={{
        marginTop: 14, borderRadius: 12,
        border: "1px dashed #2d5a8a55",
        background: "transparent",
        padding: "12px 14px",
        display: "flex", alignItems: "center", gap: 10,
      }}>
        <span style={{ fontSize: 14 }}>🎟️</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#4a6a8a" }}>Ticket creation disabled</div>
          <div style={{ fontSize: 10, color: "#2d4a6a", marginTop: 2 }}>Toggle "🎟️ ON" on the card to enable</div>
        </div>
      </div>
    );
  }

  // ── FULL FORM (ticketAllowed = true) ──────────────────────────────────────
  const minDeadlineStr = (() => { const d = new Date(); d.setMinutes(d.getMinutes() + 30); return d.toISOString().slice(0, 16); })();

  const handleToggle = () => {
    if (existingTicket) return;
    const next = !enabled;
    setEnabled(next);
    setExpanded(next);
    if (next && !deadline) setDeadline(suggestDeadline(priority));
  };

  const handlePriorityChange = (p) => {
    setPriority(p);
    setDeadline(suggestDeadline(p));
  };

  const handleCreate = async () => {
    if (!deadline) {
      await Swal.fire({ ...swalBase, icon: "warning", title: "Deadline Required", text: "Please set a deadline before creating the ticket." });
      return;
    }
    const ticketNumber = generateTicketNumber();
    const now = new Date().toISOString();
    const checklistText = Array.isArray(card.checklist) && card.checklist.length > 0
      ? "\n\nChecklist:\n" + card.checklist.map((i, idx) => `${idx + 1}. [${i.done ? "x" : " "}] ${i.text || i.label || String(i)}`).join("\n")
      : "";
    const labelsText = Array.isArray(card.labels) && card.labels.length > 0
      ? card.labels.join(", ") : "";
    const timeGoalMap = { Critical: "4 hours", High: "24 hours", Medium: "48 hours", Low: "48 hours" };
    const ticketPayload = {
      ticketNumber, subject: card.title || "Untitled Card",
      description: (card.desc || card.description || "") + checklistText,
      name: card.createdBy || "System", email: "", contact: "",
      userId: card.createdBy || "system", category: "Task Board", priority,
      status: "Open", tags: labelsText, origin: "Board",
      notes: `Created from board card. Card ID: ${card.id || ""}`,
      assignees: "", deadline: new Date(deadline).toISOString(),
      condition: "New", jql: `priority = ${priority}`,
      calendar: "Standard Hours", timeGoal: timeGoalMap[priority] || "48 hours",
      team: "", createdAt: now, dateUpdated: now, followup: false,
      attachment: null, subtask: null, assignmentRequest: "", assignmentRequestApproved: null,
    };
    setCreating(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/Tickets`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(ticketPayload),
      });
      if (!res.ok) throw new Error(await res.text().catch(() => `HTTP ${res.status}`));
      setExistingTicket(ticketNumber);
      setExpanded(false);
      onTicketCreated?.(ticketNumber);
      const pc = PRIORITY_COLORS[priority];
      await Swal.fire({
        ...swalBase, icon: "success", title: "Ticket Created! 🎉",
        html: `<div style="text-align:left;font-size:13px;color:#94a3b8;line-height:2">
          <div>🎫 <b style="color:#60a5fa;font-family:monospace">${ticketNumber}</b></div>
          <div>📌 <b style="color:#f1f5f9">${card.title}</b></div>
          <div>🔖 Priority: <b style="color:${pc.text}">${priority}</b></div>
          <div>📅 <b style="color:#60a5fa">${new Date(deadline).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}</b></div>
        </div>`,
        timer: 3500, showConfirmButton: false,
      });
    } catch (e) {
      await swalError(`Failed to create ticket: ${e.message}`);
    } finally { setCreating(false); }
  };

  const border = existingTicket ? "#4bce9744" : enabled ? "#579dff44" : (C?.border || "#1a3050");
  const bgColor = existingTicket ? "#4bce9708" : enabled ? "#579dff08" : "transparent";

  return (
    <div style={{ marginTop: 14, borderRadius: 12, border: `1px solid ${border}`, background: bgColor, overflow: "hidden", transition: "all .2s" }}>
      <div onClick={handleToggle} style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 14px", cursor: existingTicket ? "default" : "pointer", userSelect: "none" }}>
        {!existingTicket && (
          <div style={{ width: 36, height: 20, borderRadius: 10, background: enabled ? "#579dff" : "#1a3050", border: `1px solid ${enabled ? "#579dff99" : "#1e3a55"}`, position: "relative", transition: "all .2s", flexShrink: 0 }}>
            <div style={{ position: "absolute", top: 2, left: enabled ? 18 : 2, width: 14, height: 14, borderRadius: "50%", background: "#fff", transition: "left .2s", boxShadow: "0 1px 4px rgba(0,0,0,.4)" }} />
          </div>
        )}
        <span style={{ fontSize: 15 }}>{existingTicket ? "🎫" : "🎟️"}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: existingTicket ? "#4bce97" : enabled ? "#579dff" : "#4a6a8a", letterSpacing: ".03em" }}>
            {existingTicket ? "TICKET CREATED" : "CREATE TICKET"}
          </div>
          {existingTicket
            ? <div style={{ fontSize: 11, color: "#60a5fa", fontFamily: "monospace", marginTop: 1 }}>{existingTicket}</div>
            : <div style={{ fontSize: 10, color: "#4a6a8a", marginTop: 1 }}>Convert this card into a support ticket</div>
          }
        </div>
        {existingTicket && (
          <button onClick={e => { e.stopPropagation(); navigator.clipboard?.writeText(existingTicket); Swal.fire({ ...swalBase, icon: "success", title: "Copied!", timer: 900, showConfirmButton: false }); }}
            style={{ background: "#579dff18", border: "1px solid #579dff30", borderRadius: 6, color: "#579dff", fontSize: 10, padding: "3px 9px", cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>
            📋 Copy
          </button>
        )}
        {!existingTicket && enabled && (
          <span onClick={e => { e.stopPropagation(); setExpanded(p => !p); }}
            style={{ fontSize: 10, color: "#4a6a8a", cursor: "pointer", padding: "2px 4px", borderRadius: 4 }}>
            {expanded ? "▲" : "▼"}
          </span>
        )}
      </div>
      {enabled && !existingTicket && expanded && (
        <div style={{ padding: "0 14px 14px", borderTop: "1px solid #1a3050", display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ background: "#0a1628", border: "1px solid #1a3050", borderRadius: 9, padding: "10px 12px", marginTop: 10, display: "flex", flexDirection: "column", gap: 5 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#4a6a8a", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 4 }}>📦 Will be included</div>
            <TicketFieldRow icon="📌" label="Subject" value={card.title} />
            {(card.desc || card.description) && <TicketFieldRow icon="📝" label="Description" value={((card.desc || card.description) + "").slice(0, 65) + ((card.desc || card.description).length > 65 ? "…" : "")} />}
            {Array.isArray(card.checklist) && card.checklist.length > 0 && <TicketFieldRow icon="✅" label="Checklist" value={`${card.checklist.length} item${card.checklist.length !== 1 ? "s" : ""}`} />}
            {Array.isArray(card.labels) && card.labels.length > 0 && <TicketFieldRow icon="🏷️" label="Labels" value={card.labels.join(", ")} />}
            <TicketFieldRow icon="📊" label="Status" value="Open (new ticket)" />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#8aaccc", marginBottom: 7 }}>🔖 Priority</div>
            <div style={{ display: "flex", gap: 6 }}>
              {["Critical", "High", "Medium", "Low"].map(p => {
                const pc = PRIORITY_COLORS[p]; const isSel = priority === p;
                return <button key={p} onClick={() => handlePriorityChange(p)} style={{ flex: 1, padding: "6px 4px", borderRadius: 7, border: `1px solid ${isSel ? pc.border : "#1a3050"}`, background: isSel ? pc.dim : "transparent", color: isSel ? pc.text : "#4a6a8a", fontSize: 11, fontWeight: isSel ? 700 : 400, cursor: "pointer", fontFamily: "inherit", transition: "all .15s" }}>{p}</button>;
              })}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#8aaccc", marginBottom: 7, display: "flex", alignItems: "center", gap: 6 }}>
              📅 Deadline <span style={{ fontSize: 10, color: "#f87168", fontWeight: 400 }}>* required</span>
            </div>
            <input type="datetime-local" value={deadline} min={minDeadlineStr} onChange={e => setDeadline(e.target.value)}
              style={{ width: "100%", boxSizing: "border-box", padding: "8px 12px", background: "#0a1628", border: `1px solid ${deadline ? "#579dff55" : "#f8716855"}`, borderRadius: 8, color: "#dce8f5", fontSize: 12, fontFamily: "inherit", outline: "none" }} />
            {deadline && <div style={{ fontSize: 10, color: "#579dff", marginTop: 4 }}>✓ {new Date(deadline).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</div>}
          </div>
          <button onClick={handleCreate} disabled={creating || !deadline}
            style={{ width: "100%", padding: "10px", borderRadius: 9, border: "none", background: creating || !deadline ? "#1a3050" : "linear-gradient(135deg,#579dff,#3b82f6)", color: creating || !deadline ? "#4a6a8a" : "#fff", fontSize: 13, fontWeight: 700, cursor: creating || !deadline ? "not-allowed" : "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, transition: "all .15s", boxShadow: !creating && deadline ? "0 4px 16px rgba(87,157,255,.3)" : "none" }}
            onMouseEnter={e => { if (!creating && deadline) e.currentTarget.style.filter = "brightness(1.12)"; }}
            onMouseLeave={e => { e.currentTarget.style.filter = "none"; }}>
            {creating ? <><span style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid #4a6a8a", borderTop: "2px solid #579dff", animation: "ctspin .7s linear infinite", display: "inline-block" }} /> Creating ticket…</> : <>🎫 Create Ticket</>}
          </button>
        </div>
      )}
    </div>
  );
}
// ─── SearchFilterBar ──────────────────────────────────────────────────────────
function SearchFilterBar({ search, onSearch, filter, onFilter, sort, onSort, totalVisible, totalAll, C }) {
  const [sortOpen, setSortOpen] = useState(false);
  const activeSort = SORT_OPTIONS.find(s => s.key === sort);
  const hasActive = search || filter !== "all" || sort !== "default";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, minWidth: 0, flexWrap: "wrap" }}>
      <div style={{ position: "relative", minWidth: 130, maxWidth: 200 }}>
        <span style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", fontSize: 11, color: C.textMuted, pointerEvents: "none" }}>🔍</span>
        <input value={search} onChange={e => onSearch(e.target.value)} placeholder="Search cards…"
          style={{ width: "100%", boxSizing: "border-box", paddingLeft: 26, paddingRight: search ? 24 : 8, paddingTop: 5, paddingBottom: 5, background: C.surface, border: `1px solid ${search ? C.accent + "88" : C.border}`, borderRadius: 8, color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none", transition: "border .15s" }}
          onFocus={e => e.target.style.borderColor = C.accent}
          onBlur={e => e.target.style.borderColor = search ? C.accent + "88" : C.border} />
        {search && (
          <button onClick={() => onSearch("")} style={{ position: "absolute", right: 5, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: C.textMuted, cursor: "pointer", fontSize: 12, padding: 0, fontFamily: "inherit" }}>✕</button>
        )}
      </div>

      <div style={{ display: "flex", gap: 3, alignItems: "center", flexWrap: "wrap" }}>
        {FILTER_OPTIONS.map(opt => {
          const isSel = filter === opt.key;
          return (
            <button key={opt.key} onClick={() => onFilter(opt.key)}
              style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "4px 9px", borderRadius: 7, border: `1px solid ${isSel ? (opt.color || C.accent) + "99" : C.border}`, background: isSel ? `${opt.color || C.accent}22` : "transparent", color: isSel ? (opt.color || C.accent) : C.textMuted, fontSize: 11, fontWeight: isSel ? 700 : 400, cursor: "pointer", fontFamily: "inherit", transition: "all .12s", whiteSpace: "nowrap" }}
              onMouseEnter={e => { if (!isSel) { e.currentTarget.style.background = C.surface; e.currentTarget.style.color = C.textMid; } }}
              onMouseLeave={e => { if (!isSel) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = C.textMuted; } }}>
              <span style={{ fontSize: 10 }}>{opt.icon}</span> {opt.label}
            </button>
          );
        })}
      </div>

      <div style={{ position: "relative" }}>
        <button onClick={() => setSortOpen(p => !p)}
          style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 7, border: `1px solid ${sort !== "default" ? C.accent + "88" : C.border}`, background: sort !== "default" ? `${C.accent}18` : "transparent", color: sort !== "default" ? C.accent : C.textMuted, fontSize: 11, fontWeight: sort !== "default" ? 700 : 400, cursor: "pointer", fontFamily: "inherit", transition: "all .12s" }}>
          <span style={{ fontSize: 10 }}>{activeSort?.icon}</span>
          Sort{sort !== "default" ? `: ${activeSort?.label}` : ""}
          <span style={{ fontSize: 8, opacity: .6 }}>{sortOpen ? "▲" : "▼"}</span>
        </button>
        {sortOpen && (
          <>
            <div onClick={() => setSortOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 500 }} />
            <div style={{ position: "absolute", bottom: "110%", left: 0, zIndex: 600, background: C.colBg, border: `1px solid ${C.borderHov}`, borderRadius: 10, padding: 6, minWidth: 148, boxShadow: "0 -12px 32px rgba(0,0,0,.75)" }}>
              {SORT_OPTIONS.map(opt => (
                <div key={opt.key} onClick={() => { onSort(opt.key); setSortOpen(false); }}
                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", borderRadius: 6, cursor: "pointer", background: sort === opt.key ? `${C.accent}18` : "transparent", color: sort === opt.key ? C.accent : C.textMid, fontSize: 12, fontWeight: sort === opt.key ? 600 : 400 }}
                  onMouseEnter={e => { if (sort !== opt.key) e.currentTarget.style.background = C.surface; }}
                  onMouseLeave={e => { if (sort !== opt.key) e.currentTarget.style.background = "transparent"; }}>
                  <span>{opt.icon}</span> {opt.label}
                  {sort === opt.key && <span style={{ marginLeft: "auto" }}>✓</span>}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {hasActive && (
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <span style={{ fontSize: 11, color: C.textDim }}>{totalVisible}/{totalAll}</span>
          <button onClick={() => { onSearch(""); onFilter("all"); onSort("default"); }}
            style={{ fontSize: 10, color: C.red, background: `${C.red}15`, border: `1px solid ${C.red}30`, borderRadius: 6, padding: "2px 7px", cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>
            Clear ✕
          </button>
        </div>
      )}
    </div>
  );
}

// ─── BoardSettingsPanel ───────────────────────────────────────────────────────
function BoardSettingsPanel({ theme, cardSize, bgOverride, onTheme, onCardSize, onBg, onBgUpload, C }) {
  const [tab, setTab] = useState("theme");
  const [open, setOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef();
  const tabs = [{ id: "theme", label: "🎨 Theme" }, { id: "bg", label: "🖼️ Background" }, { id: "size", label: "📐 Card Size" }];

  const handleFileUpload = (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = (e) => onBgUpload(e.target.result);
    reader.readAsDataURL(file);
  };

  return (
    <div style={{ position: "fixed", bottom: 16, left: "50%", transform: "translateX(-50%)", zIndex: 9999, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
      <button onClick={() => setOpen(p => !p)}
        style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 10, border: `1px solid ${open ? C.borderHov : C.border}`, background: open ? `${C.accent}18` : C.surface, color: open ? C.accent : C.textMid, fontSize: 12, fontWeight: 600, transform: "translateY(-150%)", cursor: "pointer", fontFamily: "inherit", transition: "all .15s", userSelect: "none" }}>
        ⚙️ Board Settings {open ? "▲" : "▼"}
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 300, cursor: "default" }} />
          <div style={{ position: "absolute", bottom: "110%", left: "50%", transform: "translateX(-50%)", zIndex: 400, background: C.colBg, border: `1px solid ${C.borderHov}`, borderRadius: 16, width: 340, boxShadow: "0 -16px 48px rgba(0,0,0,.7)", overflow: "hidden", userSelect: "none" }}>
            <div style={{ display: "flex", borderBottom: `1px solid ${C.border}` }}>
              {tabs.map(t => (
                <button key={t.id} onClick={() => setTab(t.id)}
                  style={{ flex: 1, padding: "11px 4px", border: "none", background: tab === t.id ? `${C.accent}18` : "transparent", color: tab === t.id ? C.accent : C.textMuted, fontSize: 11, fontWeight: tab === t.id ? 700 : 400, cursor: "pointer", fontFamily: "inherit", borderBottom: tab === t.id ? `2px solid ${C.accent}` : "2px solid transparent", transition: "all .15s" }}>
                  {t.label}
                </button>
              ))}
            </div>
            <div style={{ padding: 14 }}>
              {tab === "theme" && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {Object.entries(THEMES).map(([key, t]) => (
                    <div key={key} onClick={() => { onTheme(key); setOpen(false); }}
                      style={{ padding: "10px 12px", borderRadius: 10, cursor: "pointer", border: `1px solid ${theme === key ? C.accent + "99" : C.border}`, background: theme === key ? `${C.accent}18` : C.surface, display: "flex", alignItems: "center", gap: 8, transition: "all .15s" }}
                      onMouseEnter={e => { if (theme !== key) e.currentTarget.style.background = C.surfaceHov; }}
                      onMouseLeave={e => { if (theme !== key) e.currentTarget.style.background = C.surface; }}>
                      <span style={{ fontSize: 18 }}>{t.icon}</span>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{t.name}</div>
                        <div style={{ display: "flex", gap: 3, marginTop: 4 }}>
                          {[t.bg, t.colBg, t.accent, t.cardBg].map((c, i) => <div key={i} style={{ width: 10, height: 10, borderRadius: "50%", background: c }} />)}
                        </div>
                      </div>
                      {theme === key && <span style={{ marginLeft: "auto", color: C.accent, fontSize: 14 }}>✓</span>}
                    </div>
                  ))}
                </div>
              )}
              {tab === "bg" && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>Presets</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 6, marginBottom: 12 }}>
                    {BG_PRESETS.map(p => {
                      const isSel = bgOverride === p.value;
                      return (
                        <div key={p.label} onClick={() => { onBg(p.value); setOpen(false); }}
                          style={{ height: 44, borderRadius: 8, cursor: "pointer", background: p.value || C.bg, border: `2px solid ${isSel ? C.accent : C.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 600, color: "#fff", textShadow: "0 1px 4px rgba(0,0,0,.8)", transition: "all .15s" }}
                          onMouseEnter={e => e.currentTarget.style.border = `2px solid ${C.borderHov}`}
                          onMouseLeave={e => e.currentTarget.style.border = `2px solid ${isSel ? C.accent : C.border}`}>
                          {p.label}
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>Custom Image</div>
                  <div onClick={() => fileRef.current?.click()}
                    onDrop={e => { e.preventDefault(); setDragging(false); handleFileUpload(e.dataTransfer.files?.[0]); setOpen(false); }}
                    onDragOver={e => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    style={{ border: `2px dashed ${dragging ? C.accent : C.border}`, borderRadius: 10, padding: "16px 12px", display: "flex", flexDirection: "column", alignItems: "center", gap: 6, cursor: "pointer", background: dragging ? `${C.accent}08` : "transparent", transition: "all .15s" }}>
                    <span style={{ fontSize: 22 }}>🖼️</span>
                    <span style={{ fontSize: 12, color: C.textMuted }}>Click or drag image to upload</span>
                    <span style={{ fontSize: 10, color: C.textDim }}>Saved locally in your browser</span>
                  </div>
                  <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={e => { handleFileUpload(e.target.files?.[0]); e.target.value = ""; setOpen(false); }} />
                  {bgOverride && (
                    <button onClick={() => { onBg(null); onBgUpload(null); }}
                      style={{ marginTop: 8, width: "100%", padding: "8px", borderRadius: 8, border: `1px solid ${C.border}`, background: "transparent", color: C.textMuted, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>
                      ✕ Remove background
                    </button>
                  )}
                </div>
              )}
              {tab === "size" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>Choose board column width:</div>
                  {Object.entries(CARD_SIZES).map(([key, s]) => (
                    <div key={key} onClick={() => { onCardSize(key); setOpen(false); }}
                      style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: 10, cursor: "pointer", border: `1px solid ${cardSize === key ? C.accent + "88" : C.border}`, background: cardSize === key ? `${C.accent}15` : C.surface, transition: "all .15s" }}
                      onMouseEnter={e => { if (cardSize !== key) e.currentTarget.style.background = C.surfaceHov; }}
                      onMouseLeave={e => { if (cardSize !== key) e.currentTarget.style.background = C.surface; }}>
                      <span style={{ fontSize: 20, color: cardSize === key ? C.accent : C.textMid }}>{s.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{s.name}</div>
                        <div style={{ fontSize: 11, color: C.textMuted }}>{s.width}px column width</div>
                      </div>
                      <div style={{ display: "flex", gap: 3, alignItems: "flex-end" }}>
                        {[1, 1, 1].map((_, i) => <div key={i} style={{ width: s.width / 80, height: 24 + i * 4, borderRadius: 3, background: cardSize === key ? `${C.accent}66` : C.border }} />)}
                      </div>
                      {cardSize === key && <span style={{ color: C.accent, fontSize: 14, marginLeft: 4 }}>✓</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── ColorPicker ──────────────────────────────────────────────────────────────
function ColorPicker({ current, onChange, onClose, C }) {
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 200 }} />
      <div style={{ position: "absolute", top: "130%", left: 0, zIndex: 201, background: C.colBg, border: `1px solid ${C.borderHov}`, borderRadius: 12, padding: 10, display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 7, boxShadow: "0 16px 40px rgba(0,0,0,.8)", minWidth: 186 }}>
        <div style={{ gridColumn: "1/-1", fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 2 }}>Pick color</div>
        {COLOR_PALETTE.map(c => (
          <div key={c} onClick={e => { e.stopPropagation(); onChange(c); onClose(); }}
            style={{ width: 24, height: 24, borderRadius: "50%", background: c, cursor: "pointer", border: current === c ? "2px solid #fff" : "2px solid transparent", boxShadow: current === c ? `0 0 0 3px ${c}55` : "none", transition: "transform .12s" }}
            onMouseEnter={e => e.currentTarget.style.transform = "scale(1.3)"}
            onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"} />
        ))}
      </div>
    </>
  );
}

// ─── AssignBoardModal ─────────────────────────────────────────────────────────
function AssignBoardModal({ admins, currentUser, onConfirm, onClose, C }) {
  const [selected, setSelected] = useState(currentUser.username);
  const options = [
    { username: currentUser.username, fullNames: currentUser.fullNames, role: currentUser.role, isMe: true },
    ...admins.filter(a => a.username !== currentUser.username).map(a => ({ username: a.username, fullNames: a.fullNames || a.username, role: a.role || "admin", isMe: false })),
  ];
  const selectedName = options.find(o => o.username === selected)?.fullNames || selected;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,.8)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: C.colBg, border: `1px solid ${C.borderHov}`, borderRadius: 20, width: "100%", maxWidth: 420, boxShadow: "0 24px 60px rgba(0,0,0,.85)", overflow: "hidden" }}>
        <div style={{ padding: "20px 22px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>👤 Assign Board</div>
            <div style={{ fontSize: 12, color: C.textMuted, marginTop: 3 }}>Choose who this board belongs to</div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,.08)", border: "none", borderRadius: 8, color: C.textMid, fontSize: 18, width: 32, height: 32, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
        </div>
        <div style={{ padding: "14px 16px", maxHeight: 340, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
          {options.map(opt => {
            const isSel = selected === opt.username;
            return (
              <div key={opt.username} onClick={() => setSelected(opt.username)}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 12, cursor: "pointer", border: `1px solid ${isSel ? C.accent + "88" : C.border}`, background: isSel ? `${C.accent}15` : C.surface, transition: "all .15s" }}
                onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = C.surfaceHov; }}
                onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = C.surface; }}>
                <div style={{ width: 38, height: 38, borderRadius: "50%", background: isSel ? `${C.accent}33` : C.surface, border: `2px solid ${isSel ? C.accent + "66" : C.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 700, color: isSel ? C.accent : C.textMid, flexShrink: 0 }}>
                  {opt.fullNames[0]?.toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text, display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{opt.fullNames}</span>
                    {opt.isMe && <span style={{ fontSize: 10, color: C.accent, background: `${C.accent}20`, border: `1px solid ${C.accent}40`, borderRadius: 20, padding: "0 6px", flexShrink: 0 }}>me</span>}
                  </div>
                  <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{opt.role} · {opt.username}</div>
                </div>
                {isSel && <div style={{ width: 22, height: 22, borderRadius: "50%", background: C.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "#fff", flexShrink: 0 }}>✓</div>}
              </div>
            );
          })}
        </div>
        <div style={{ padding: "14px 16px", borderTop: `1px solid ${C.border}`, display: "flex", gap: 8 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "11px", borderRadius: 10, border: `1px solid ${C.border}`, background: "transparent", color: C.textMid, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
          <button onClick={() => onConfirm(selected)} style={{ flex: 2, padding: "11px", borderRadius: 10, border: "none", background: C.accent, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
            onMouseEnter={e => e.currentTarget.style.filter = "brightness(1.15)"}
            onMouseLeave={e => e.currentTarget.style.filter = "none"}>
            ✓ Assign to {selectedName}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── AttachmentPanel ──────────────────────────────────────────────────────────
function AttachmentPanel({ cardId, onCountChange, C }) {
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileRef = useRef();
  const dlBase = `${config.API_BASE_URL}/cards/attachments`;

  useEffect(() => {
    if (!cardId) return;
    fetch(`${config.API_BASE_URL}/cards/${cardId}/attachments`).then(r => r.ok ? r.json() : []).then(d => { setAttachments(d); onCountChange?.(d.length); }).catch(() => { });
  }, [cardId]);

  const uploadFile = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("uploadedBy", getUser().username);
    setUploading(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/cards/${cardId}/attachments`, { method: "POST", body: fd });
      if (!res.ok) throw new Error(await res.text());
      const saved = await res.json();
      const upd = [...attachments, saved]; setAttachments(upd); onCountChange?.(upd.length);
    } catch (e) { await swalError(`Upload failed: ${e.message}`); }
    setUploading(false);
  };

  const handleDelete = async (id) => {
    if (!await swalConfirm("Remove attachment?", "This cannot be undone.", "Remove")) return;
    try {
      await fetch(`${dlBase}/${id}`, { method: "DELETE" });
      const upd = attachments.filter(a => a.id !== id); setAttachments(upd); onCountChange?.(upd.length);
    } catch { await swalError("Failed to delete."); }
  };

  const fileIcon = (ct) => { if (ct?.includes("pdf")) return "📄"; if (ct?.includes("word")) return "📝"; if (ct?.includes("sheet") || ct?.includes("excel")) return "📊"; if (ct?.includes("zip")) return "🗜️"; if (ct?.includes("video")) return "🎬"; return "📁"; };

  return (
    <>
      {previewing && (
        <div onClick={() => setPreviewing(null)} style={{ position: "fixed", inset: 0, zIndex: 99999, background: "rgba(0,0,0,.92)", backdropFilter: "blur(8px)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, cursor: "zoom-out" }}>
          <img src={previewing.url} alt={previewing.name} onClick={e => e.stopPropagation()} style={{ maxWidth: "88vw", maxHeight: "78vh", borderRadius: 12, objectFit: "contain" }} />
          <div style={{ display: "flex", gap: 12 }}>
            <a href={previewing.url} download={previewing.name} onClick={e => e.stopPropagation()} style={{ background: "#579dff", color: "#fff", borderRadius: 8, padding: "6px 16px", fontSize: 12, fontWeight: 600, textDecoration: "none" }}>↓ Download</a>
            <button onClick={() => setPreviewing(null)} style={{ background: "rgba(255,255,255,.1)", border: "none", borderRadius: 8, color: "#fff", padding: "6px 14px", cursor: "pointer", fontFamily: "inherit" }}>✕ Close</button>
          </div>
        </div>
      )}
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: C.textMid, textTransform: "uppercase", letterSpacing: ".06em" }}>📎 Attachments {attachments.length > 0 && `(${attachments.length})`}</span>
          <button onClick={() => fileRef.current?.click()} disabled={uploading} style={{ background: `${C.accent}20`, border: `1px solid ${C.accent}44`, borderRadius: 7, color: C.accent, fontSize: 11, fontWeight: 600, padding: "5px 13px", cursor: "pointer", fontFamily: "inherit" }}>{uploading ? "Uploading…" : "+ Add"}</button>
          <input ref={fileRef} type="file" accept="*/*" style={{ display: "none" }} onChange={e => { uploadFile(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
        {attachments.length === 0 && (
          <div onClick={() => fileRef.current?.click()} onDrop={e => { e.preventDefault(); setDragActive(false); uploadFile(e.dataTransfer.files?.[0]); }} onDragOver={e => { e.preventDefault(); setDragActive(true); }} onDragLeave={() => setDragActive(false)}
            style={{ border: `2px dashed ${dragActive ? C.accent : C.border}`, borderRadius: 12, padding: "28px 20px", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <span style={{ fontSize: 28 }}>📎</span>
            <span style={{ fontSize: 12, color: C.textMuted }}>Drag & drop or click to attach</span>
          </div>
        )}
        {attachments.length > 0 && (
          <>
            {attachments.some(a => isImage(a.contentType)) && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(110px,1fr))", gap: 8, marginBottom: 10 }}>
                {attachments.filter(a => isImage(a.contentType)).map(att => (
                  <div key={att.id} style={{ position: "relative", borderRadius: 10, overflow: "hidden", aspectRatio: "4/3", cursor: "pointer" }} onClick={() => setPreviewing({ url: `${dlBase}/${att.id}/download`, name: att.fileName })}>
                    <img src={`${dlBase}/${att.id}/download`} alt={att.fileName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    <button onClick={e => { e.stopPropagation(); handleDelete(att.id); }} style={{ position: "absolute", top: 4, right: 4, background: "rgba(248,113,104,.85)", border: "none", borderRadius: 4, color: "#fff", fontSize: 9, padding: "2px 5px", cursor: "pointer" }}>✕</button>
                  </div>
                ))}
              </div>
            )}
            {attachments.filter(a => !isImage(a.contentType)).map(att => (
              <div key={att.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 9, padding: "9px 12px", marginBottom: 6, display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: `${C.accent}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>{fileIcon(att.contentType)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{att.fileName}</div>
                  <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>{fmtSize(att.fileSize)} · {att.uploadedBy}</div>
                </div>
                <a href={`${dlBase}/${att.id}/download`} target="_blank" rel="noreferrer" style={{ background: `${C.accent}18`, border: `1px solid ${C.accent}30`, borderRadius: 6, color: C.accent, fontSize: 11, padding: "4px 10px", textDecoration: "none" }}>↓</a>
                <button onClick={() => handleDelete(att.id)} style={{ background: `${C.red}18`, border: `1px solid ${C.red}30`, borderRadius: 6, color: C.red, fontSize: 11, padding: "4px 9px", cursor: "pointer", fontFamily: "inherit" }}>✕</button>
              </div>
            ))}
          </>
        )}
      </div>
    </>
  );
}

// ─── StatusBadge ─────────────────────────────────────────────────────────────
function StatusBadge({ status, onChange, C }) {
  const [open, setOpen] = useState(false);
  const s = STATUS_MAP[status] ?? STATUS_MAP.todo;
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <div onClick={e => { e.stopPropagation(); setOpen(p => !p); }} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: `${s.color}22`, color: s.color, border: `1px solid ${s.color}44`, cursor: onChange ? "pointer" : "default", userSelect: "none" }}>
        {s.emoji} {s.label} {onChange && <span style={{ fontSize: 9, opacity: .7 }}>▼</span>}
      </div>
      {open && onChange && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 90 }} />
          <div style={{ position: "absolute", top: "110%", left: 0, zIndex: 100, background: C.colBg, border: `1px solid ${C.borderHov}`, borderRadius: 10, padding: 6, minWidth: 160, boxShadow: "0 12px 32px rgba(0,0,0,.6)" }}>
            {STATUSES.map(st => (
              <div key={st.key} onClick={() => { onChange(st.key); setOpen(false); }}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", borderRadius: 6, cursor: "pointer", background: st.key === status ? `${st.color}22` : "transparent", color: st.key === status ? st.color : C.textMid, fontSize: 12, fontWeight: st.key === status ? 600 : 400 }}
                onMouseEnter={e => e.currentTarget.style.background = `${st.color}18`}
                onMouseLeave={e => e.currentTarget.style.background = st.key === status ? `${st.color}22` : "transparent"}>
                {st.emoji} {st.label} {st.key === status && <span style={{ marginLeft: "auto" }}>✓</span>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── useCoverImage ────────────────────────────────────────────────────────────
function useCoverImage(cardId) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!cardId) return;
    let dead = false;
    fetch(`${config.API_BASE_URL}/cards/${cardId}/attachments`).then(r => r.ok ? r.json() : []).then(list => { if (!dead) { const f = list.find(a => a.contentType?.startsWith("image/")); setUrl(f ? `${config.API_BASE_URL}/cards/attachments/${f.id}/download` : null); } }).catch(() => { });
    return () => { dead = true; };
  }, [cardId]);
  return url;
}

// ─── BoardCard ────────────────────────────────────────────────────────────────
function BoardCard({ card, onClick, onStatusChange, onToggleTicketAllowed, colWidth, C }) {
  const [hov, setHov] = useState(false);
  const progress = checklistProgress(card.checklist);
  const coverUrl = useCoverImage(card.id);
  const st = STATUS_MAP[card.status ?? "todo"];
  const coverH = colWidth <= 240 ? 100 : colWidth <= 300 ? 130 : 160;
  const isSuperAdmin = getUser().role === "superadmin";

  return (
    <div onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ background: hov ? C.cardHov : C.cardBg, border: `1px solid ${hov ? C.borderHov : C.border}`, borderRadius: 10, cursor: "pointer", transition: "all .18s", marginBottom: 8, transform: hov ? "translateY(-2px)" : "none", boxShadow: hov ? "0 8px 24px rgba(0,0,0,.4)" : "0 1px 4px rgba(0,0,0,.15)", overflow: "hidden" }}>
      {coverUrl && (
        <div style={{ width: "100%", height: coverH, overflow: "hidden", position: "relative" }}>
          <img src={coverUrl} alt="cover" style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform .3s", transform: hov ? "scale(1.04)" : "scale(1)" }} />
          <div onClick={e => e.stopPropagation()} style={{ position: "absolute", top: 8, left: 8 }}><StatusBadge status={card.status} onChange={onStatusChange ? ns => onStatusChange(card, ns) : null} C={C} /></div>
          {card.labels?.length > 0 && <div style={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 3, flexWrap: "wrap", maxWidth: 100, justifyContent: "flex-end" }}>{card.labels.map(l => <LabelPill key={l} color={l} small />)}</div>}
        </div>
      )}
      <div style={{ padding: "10px 12px" }}>
        {!coverUrl && card.labels?.length > 0 && <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 7 }}>{card.labels.map(l => <LabelPill key={l} color={l} small />)}</div>}
        <div style={{ fontSize: colWidth <= 240 ? 12 : 13, fontWeight: 600, color: C.text, lineHeight: 1.5, marginBottom: 8 }}>{card.title}</div>
        {!coverUrl && <div onClick={e => e.stopPropagation()} style={{ marginBottom: progress ? 8 : 0 }}><StatusBadge status={card.status} onChange={onStatusChange ? ns => onStatusChange(card, ns) : null} C={C} /></div>}
        {progress && <div style={{ marginTop: coverUrl ? 0 : 8 }}><ChecklistBar pct={progress.pct} compact /></div>}

        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 9, paddingTop: 8, borderTop: `1px solid ${C.border}`, flexWrap: "wrap" }}>
          {progress && <span style={{ fontSize: 10, color: progress.pct === 100 ? "#4bce97" : C.textDim, background: progress.pct === 100 ? "rgba(75,206,151,.12)" : "transparent", padding: "1px 5px", borderRadius: 4 }}>✓ {card.checklist.filter(i => i.done).length}/{card.checklist.length}</span>}
          {card.desc && <span style={{ fontSize: 11, color: C.textDim }}>📝</span>}
          {card.attachments?.length > 0 && <span style={{ fontSize: 11, color: C.textDim }}>📎 {card.attachments.length}</span>}

          {/* ── Ticket badge (if linked) ── */}
          {card.ticketRef && (
            <span title={`Ticket: ${card.ticketRef}`} style={{ fontSize: 10, color: "#60a5fa", background: "rgba(96,165,250,.12)", border: "1px solid rgba(96,165,250,.22)", borderRadius: 5, padding: "1px 6px", fontFamily: "monospace", flexShrink: 0, maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              🎫 {card.ticketRef}
            </span>
          )}

          {/* ── TicketAllowed toggle (superadmin only) ── */}
          {isSuperAdmin && onToggleTicketAllowed && (
            <div onClick={e => { e.stopPropagation(); onToggleTicketAllowed(card); }}
              title={card.ticketAllowed ? "Click to disable ticket creation" : "Click to allow ticket creation"}
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                fontSize: 10, fontWeight: 700, letterSpacing: ".02em",
                color: card.ticketAllowed ? "#4bce97" : "#4a6a8a",
                background: card.ticketAllowed ? "rgba(75,206,151,.12)" : "rgba(74,106,138,.08)",
                border: `1px solid ${card.ticketAllowed ? "#4bce9744" : "#1e3a5580"}`,
                borderRadius: 5, padding: "2px 7px", cursor: "pointer",
                transition: "all .15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.filter = "brightness(1.3)"; }}
              onMouseLeave={e => { e.currentTarget.style.filter = "none"; }}>
              {card.ticketAllowed ? "🎟️ ON" : "🔒 OFF"}
            </div>
          )}

          {card.createdBy && (
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 5 }}>
              <div style={{ width: 20, height: 20, borderRadius: "50%", background: st.color + "33", border: `1px solid ${st.color}55`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, color: st.color }}>{card.createdBy[0]?.toUpperCase()}</div>
              <span style={{ fontSize: 10, color: C.textMuted, fontStyle: "italic" }}>{card.createdBy}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
// ─── StatusLane ───────────────────────────────────────────────────────────────
function StatusLane({ statusKey, cards, colId, onCardClick, onStatusChange, onToggleTicketAllowed, onDrop, colWidth, C }) {
  const [dragOver, setDragOver] = useState(false);
  const st = STATUS_MAP[statusKey];
  return (
    <div onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={() => { setDragOver(false); onDrop(colId, statusKey); }}
      style={{ background: dragOver ? `${st.color}0a` : "transparent", border: `1px dashed ${dragOver ? st.color + "66" : C.border + "44"}`, borderRadius: 8, padding: "6px 4px", marginBottom: 6, minHeight: 36, transition: "all .15s" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "2px 4px 6px", opacity: .7 }}>
        <span style={{ fontSize: 11 }}>{st.emoji}</span>
        <span style={{ fontSize: 10, fontWeight: 700, color: st.color, textTransform: "uppercase", letterSpacing: ".06em" }}>{st.label}</span>
        <span style={{ fontSize: 10, color: C.textMuted, background: C.surface, borderRadius: 8, padding: "0 5px" }}>{cards.length}</span>
      </div>
      {cards.map(card => (
        <div key={card.id} draggable onDragStart={() => { window._dragCard = { cardId: card.id, fromCol: colId, fromStatus: statusKey }; }}>
          <BoardCard card={card} onClick={() => onCardClick(card)} onStatusChange={onStatusChange}
            onToggleTicketAllowed={onToggleTicketAllowed}
            colWidth={colWidth} C={C} />
        </div>
      ))}
      {cards.length === 0 && <div style={{ textAlign: "center", color: C.textMuted + "55", fontSize: 11, padding: "8px 0", userSelect: "none" }}>Drop here</div>}
    </div>
  );
}

// ─── Column ───────────────────────────────────────────────────────────────────
function Column({ col, cards, onAddCard, onCardClick, onDrop, onRename, onDeleteCol, onStatusChange, onToggleTicketAllowed, onColorChange, colWidth, C }) {
  const [addingCard, setAddingCard] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleVal, setTitleVal] = useState(col.title);
  const [menuOpen, setMenuOpen] = useState(false);
  const [colorPickOpen, setColorPickOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const colColor = resolveColor(col.id, col.color);

  const commitRename = async () => {
    const val = titleVal?.trim();
    if (val && val !== col.title) await onRename(col.id, val);
    else setTitleVal(col.title);
    setEditingTitle(false);
  };
  const handleColorChange = (newColor) => { saveColorOverride(col.id, newColor); onColorChange(col.id, newColor); };

  return (
    <div onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={() => { setDragOver(false); onDrop(col.id, null); }}
      style={{ width: colWidth, flexShrink: 0, display: "flex", flexDirection: "column", maxHeight: "100%", background: C.colBg, borderRadius: 14, border: `1px solid ${dragOver ? colColor + "55" : C.border}`, boxShadow: dragOver ? `0 0 0 2px ${colColor}33` : "0 4px 16px rgba(0,0,0,.25)", transition: "all .15s", overflow: "hidden" }}>
      <div style={{ height: 4, background: `linear-gradient(90deg, ${colColor}, ${colColor}66)`, flexShrink: 0 }} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px 10px", borderBottom: `1px solid ${C.border}`, background: C.colHeader, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
          <div style={{ position: "relative", flexShrink: 0 }}>
            <div onClick={() => { setMenuOpen(false); setColorPickOpen(p => !p); }} title="Change color"
              style={{ width: 13, height: 13, borderRadius: "50%", background: colColor, boxShadow: `0 0 8px ${colColor}99`, cursor: "pointer", border: "2px solid rgba(255,255,255,.18)", transition: "transform .15s" }}
              onMouseEnter={e => e.currentTarget.style.transform = "scale(1.4)"}
              onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"} />
            {colorPickOpen && <ColorPicker current={colColor} onChange={handleColorChange} onClose={() => setColorPickOpen(false)} C={C} />}
          </div>
          {editingTitle ? (
            <input autoFocus value={titleVal} onChange={e => setTitleVal(e.target.value)} onBlur={commitRename}
              onKeyDown={e => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") { setTitleVal(col.title); setEditingTitle(false); } }}
              style={{ background: C.surface, border: `1px solid ${colColor}`, borderRadius: 6, padding: "3px 8px", fontSize: 13, fontWeight: 700, color: C.text, outline: "none", fontFamily: "inherit", width: colWidth - 120, boxSizing: "border-box" }} />
          ) : (
            <span onDoubleClick={() => setEditingTitle(true)} title="Double-click to rename"
              style={{ fontSize: 13, fontWeight: 700, color: C.text, cursor: "default", userSelect: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {col.title}
            </span>
          )}
          <span style={{ background: C.surface, color: C.textMuted, fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 12, flexShrink: 0 }}>{cards.length}</span>
          {col.assignedTo && col.assignedTo !== col.createdBy && (
            <span title={`Assigned to ${col.assignedTo}`} style={{ fontSize: 10, color: colColor, background: `${colColor}18`, border: `1px solid ${colColor}30`, borderRadius: 20, padding: "1px 7px", flexShrink: 0, maxWidth: 72, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              👤 {col.assignedTo}
            </span>
          )}
        </div>
        <div style={{ position: "relative", flexShrink: 0 }}>
          <div onClick={() => { setColorPickOpen(false); setMenuOpen(p => !p); }}
            onMouseEnter={e => e.currentTarget.style.background = C.surface}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            style={{ color: C.textDim, cursor: "pointer", fontSize: 18, padding: "2px 6px", borderRadius: 6, userSelect: "none", lineHeight: 1 }}>···</div>
          {menuOpen && (
            <>
              <div onClick={() => setMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 15 }} />
              <div style={{ position: "absolute", right: 0, top: "110%", zIndex: 20, background: C.colBg, border: `1px solid ${C.borderHov}`, borderRadius: 10, padding: 6, width: 170, boxShadow: "0 12px 32px rgba(0,0,0,.6)" }}>
                <MenuItem label="✏️  Rename" onClick={() => { setMenuOpen(false); setEditingTitle(true); }} C={C} />
                <MenuItem label="🎨  Change color" onClick={() => { setMenuOpen(false); setColorPickOpen(true); }} C={C} />
                <div style={{ height: 1, background: C.border, margin: "4px 6px" }} />
                <MenuItem label="🗑️  Delete list" onClick={() => { setMenuOpen(false); onDeleteCol(col.id); }} danger C={C} />
              </div>
            </>
          )}
        </div>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "10px 8px 4px", minHeight: 40 }}>
        {STATUSES.map(st => (
          <StatusLane key={st.key} statusKey={st.key} colId={col.id} colWidth={colWidth}
            cards={cards.filter(c => (c.status ?? "todo") === st.key)}
            onCardClick={onCardClick} onStatusChange={onStatusChange}
            onToggleTicketAllowed={onToggleTicketAllowed}   // ← FIX
            onDrop={onDrop} C={C} />
        ))}
      </div>
      <div style={{ padding: "6px 10px 10px", flexShrink: 0, borderTop: `1px solid ${C.border}` }}>
        {addingCard
          ? <AddCardInline colId={col.id} onAdd={onAddCard} onCancel={() => setAddingCard(false)} />
          : <AddCardBtnRow onClick={() => setAddingCard(true)} C={C} />}
      </div>
    </div>
  );
}

// ─── IntegratedCardModal ──────────────────────────────────────────────────────
// REPLACE the existing IntegratedCardModal in BoardView.jsx with this version.
// Key change: finds the column's assignedTo and passes it to CardModal so
// CreateTicketSection can auto-assign the ticket to the right person.

function IntegratedCardModal({ card, columns, onClose, onUpdate, onDelete, onArchive, C }) {
  const [localCard, setLocalCard] = useState(card);

  useEffect(() => setLocalCard(card), [card]);

  // ── Find which column this card belongs to ──
  const parentColumn = columns.find(col => col.id === localCard.colId);
  // assignedTo: whoever the column is assigned to (the person responsible)
  const assignedTo = parentColumn?.assignedTo || parentColumn?.createdBy || null;

  const handleStatusChange = async (ns) => {
    const u = { ...localCard, status: ns };
    setLocalCard(u);
    await onUpdate(u);
  };

  const handleUpdate = (u) => {
    const s = fromApiCard(u);
    setLocalCard(s);
    onUpdate(u);
  };

  return (
    <CardModal
      card={localCard}
      columns={columns}
      onClose={onClose}
      onUpdate={handleUpdate}
      onDelete={onDelete}
      onArchive={onArchive ? (id) => onArchive(id ?? localCard.id) : undefined}
      assignedTo={assignedTo}   // ← auto-fills Assignees field in ticket
      statusBadge={
        <StatusBadge
          status={localCard.status}
          onChange={handleStatusChange}
          C={C}
        />
      }
    />
  );
}
// ─── Small components ─────────────────────────────────────────────────────────
function MenuItem({ label, onClick, danger, C }) {
  const [hov, setHov] = useState(false);
  return <div onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={{ padding: "8px 10px", borderRadius: 6, cursor: "pointer", fontSize: 12, color: danger ? C.red : (hov ? C.text : C.textMid), background: hov ? (danger ? `${C.red}18` : C.surfaceHov) : "transparent" }}>{label}</div>;
}
function AddCardBtnRow({ onClick, C }) {
  const [hov, setHov] = useState(false);
  return <div onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 10px", borderRadius: 8, cursor: "pointer", color: hov ? C.textMid : C.textMuted, background: hov ? C.surfaceHov : "transparent", fontSize: 13, fontWeight: 500 }}><span style={{ fontSize: 16 }}>+</span> Add a card</div>;
}
function AddColumnBtn({ onClick, isSuperAdmin, colWidth, C }) {
  const [hov, setHov] = useState(false);
  return <div onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={{ width: Math.max(200, colWidth - 60), flexShrink: 0, padding: "12px 16px", borderRadius: 14, cursor: "pointer", fontSize: 13, color: hov ? C.textMid : C.textMuted, border: `2px dashed ${hov ? C.borderHov : C.border}`, background: hov ? C.surface : "transparent", fontWeight: 500, display: "flex", alignItems: "center", gap: 8, height: "fit-content" }}><span style={{ fontSize: 18 }}>+</span>{isSuperAdmin ? "Add & assign list" : "Add a list"}</div>;
}
function EmptyBoard({ isSuperAdmin, C }) {
  return <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, color: C.textMuted, textAlign: "center", minHeight: 340 }}><div style={{ fontSize: 52 }}>📋</div><p style={{ fontSize: 16, fontWeight: 700, color: C.textDim, margin: 0 }}>No lists yet</p><p style={{ fontSize: 13, margin: 0 }}>{isSuperAdmin ? 'Click "+ Add & assign list" to get started' : 'Click "+ Add a list" to get started'}</p></div>;
}
function Loader({ C }) {
  return <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16 }}><div style={{ width: 40, height: 40, borderRadius: "50%", border: `3px solid ${C.border}`, borderTop: `3px solid ${C.accent}`, animation: "bspin .8s linear infinite" }} /><span style={{ color: C.textMuted, fontSize: 14 }}>Loading board…</span></div>;
}

// ─── BoardView (main) ─────────────────────────────────────────────────────────
export default function BoardView() {
  const [columns, setColumns] = useState([]);
  const [cards, setCards] = useState([]);
  const [openCard, setOpenCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [pendingColData, setPendingColData] = useState(null);

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("default");

  const [prefs, setPrefs] = useState(() => {
    const p = loadPrefs();
    return { theme: p.theme || "dark", bg: p.bg || null, bgImage: p.bgImage || null, cardSize: p.cardSize || "normal" };
  });

  const C = THEMES[prefs.theme] || THEMES.dark;
  const colWidth = CARD_SIZES[prefs.cardSize]?.width || 300;

  const updatePref = (key, value) => {
    setPrefs(prev => { const next = { ...prev, [key]: value }; savePrefs(next); return next; });
  };

  const currentUser = useRef(getUser()).current;
  const superAdmin = currentUser.role === "superadmin";

  const bgStyle = (() => {
    if (prefs.bgImage) return { backgroundImage: `url(${prefs.bgImage})`, backgroundSize: "cover", backgroundPosition: "center" };
    if (prefs.bg) return { background: prefs.bg };
    return { background: C.bg };
  })();

  useEffect(() => {
    (async () => {
      try {
        const [colsRes, cardsRes] = await Promise.all([
          fetch(`${config.API_BASE_URL}/boards`),
          fetch(`${config.API_BASE_URL}/cards`),
        ]);
        if (!colsRes.ok) throw new Error(`Boards: ${colsRes.status}`);
        if (!cardsRes.ok) throw new Error(`Cards: ${cardsRes.status}`);
        const allCols = await colsRes.json();
        const allCards = await cardsRes.json();
        const filtered = superAdmin ? allCols : allCols.filter(col => col.assignedTo === currentUser.username || col.createdBy === currentUser.username);
        setColumns(filtered.map(col => ({ ...col, color: resolveColor(col.id, col.color) })));
        setCards(allCards.map(fromApiCard));
        if (superAdmin) {
          try { const r = await fetch(`${config.API_BASE_URL}/Admin`); if (r.ok) setAdmins(await r.json()); } catch { }
        }
      } catch (e) {
        console.error("BoardView load error:", e);
        await swalError(`Failed to load: ${e.message}`);
      } finally { setLoading(false); }
    })();
  }, []);
  // Dagdagan sa BoardView (main) — bago ang return statement
  const toggleTicketAllowed = async (card) => {
    const updated = { ...card, ticketAllowed: !card.ticketAllowed };
    try {
      const res = await fetch(`${config.API_BASE_URL}/cards/${card.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(toApiCard(updated)),
      });
      if (!res.ok) throw new Error();
      const saved = fromApiCard(await res.json());
      setCards(p => p.map(c => c.id === card.id ? saved : c));
      Swal.fire({
        ...swalBase,
        icon: "success",
        title: saved.ticketAllowed ? "🎟️ Ticket Creation Enabled!" : "🔒 Ticket Creation Disabled",
        html: `<span style="color:#94a3b8;font-size:13px">Card: <b style="color:#f1f5f9">${card.title}</b></span>`,
        timer: 1800, showConfirmButton: false,
      });
    } catch { await swalError("Failed to update ticket permission."); }
  };
  // At sa Column render — dagdagan ng prop:
  // onToggleTicketAllowed={toggleTicketAllowed}
  const activeCards = useMemo(() => cards.filter(c => !c.archived), [cards]);

  const visibleColumns = useMemo(() => {
    let result = columns;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(col =>
        col.title?.toLowerCase().includes(q) ||
        col.assignedTo?.toLowerCase().includes(q) ||
        col.createdBy?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [columns, search]);

  const addColumn = async () => {
    const title = await swalPrompt("New list name", "e.g. Sprint 1");
    if (!title) return;
    const color = COLOR_PALETTE[columns.length % COLOR_PALETTE.length];
    if (superAdmin) { setPendingColData({ title, color }); setShowAssignModal(true); }
    else { await createColumn(title, color, currentUser.username); }
  };

  const createColumn = async (title, color, assignedTo) => {
    const payload = { title: title.trim(), color, createdBy: currentUser.username, assignedTo: assignedTo || currentUser.username };
    try {
      const res = await fetch(`${config.API_BASE_URL}/boards`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error(await res.text().catch(() => `HTTP ${res.status}`));
      const saved = await res.json();
      setColumns(prev => [...prev, { ...saved, color: resolveColor(saved.id, saved.color) }]);
    } catch (e) { await swalError(`Failed to add list: ${e.message}`); }
  };

  const handleAssignConfirm = async (assignedTo) => {
    setShowAssignModal(false);
    if (!pendingColData) return;
    const { title, color } = pendingColData;
    setPendingColData(null);
    await createColumn(title, color, assignedTo);
  };

  const addCard = async (colId, title) => {
    if (!title?.trim()) return;
    const payload = toApiCard({ colId, title: title.trim(), desc: "", labels: [], checklist: [], archived: false, status: "todo", createdBy: currentUser.username });
    try {
      const res = await fetch(`${config.API_BASE_URL}/cards`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error(await res.text().catch(() => `HTTP ${res.status}`));
      const saved = await res.json();
      setCards(p => [...p, fromApiCard(saved)]);
    } catch (e) { await swalError(`Failed to add card: ${e.message}`); }
  };

  const updateCard = async (updated) => {
    const statusMap = {
      todo: "Open",
      pending: "Pending",
      inprogress: "In Progress",
      complete: "Resolved",
    };
    try {
      const res = await fetch(`${config.API_BASE_URL}/cards/${updated.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(toApiCard(updated)),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const saved = fromApiCard(await res.json());
      setCards(p => p.map(c => c.id === updated.id ? saved : c));
      if (openCard?.id === updated.id) setOpenCard(saved);

      // ✅ Sync ticket if linked
      if (updated.ticketRef && updated.status) {
        const ticketStatus = statusMap[updated.status];
        if (ticketStatus) {
          try {
            const ticketFetch = await fetch(`${config.API_BASE_URL}/Tickets/${updated.ticketRef}`);
            if (ticketFetch.ok) {
              const existingTicket = await ticketFetch.json();
              fetch(`${config.API_BASE_URL}/Tickets/${updated.ticketRef}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...existingTicket, status: ticketStatus }),
              }).then(r => {
                if (r.ok) console.log(`%c✅ Ticket synced%c ${updated.ticketRef} → ${ticketStatus}`,
                  "color:#4bce97;font-weight:bold", "color:#94a3b8");
              }).catch(e => console.warn("Ticket sync failed:", e));
            }
          } catch (e) {
            console.warn("Ticket sync error:", e);
          }
        }
      }
    } catch (e) {
      await swalError(`Failed to update: ${e.message}`);
    }
  };

  const updateCardStatus = async (card, newStatus) => {
    const statusMap = {
      todo: "Open",
      pending: "Pending",
      inprogress: "In Progress",
      complete: "Resolved",
    };

    try {
      // 1. Update card status
      const res = await fetch(`${config.API_BASE_URL}/cards/${card.id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newStatus),
      });
      if (!res.ok) throw new Error();
      const saved = fromApiCard(await res.json());
      setCards(p => p.map(c => c.id === card.id ? saved : c));
      if (openCard?.id === card.id) setOpenCard(saved);

      // 2. ✅ Use BOTH card.ticketRef AND saved.ticketRef as fallback
      const ticketRef = card.ticketRef || saved.ticketRef;
      const ticketStatus = statusMap[newStatus];

      console.log(`🔍 ticketRef: ${ticketRef}, newStatus: ${newStatus}, ticketStatus: ${ticketStatus}`);

      if (ticketRef && ticketStatus) {
        try {
          const ticketFetch = await fetch(`${config.API_BASE_URL}/Tickets/${ticketRef}`);
          console.log(`📡 Fetching ticket ${ticketRef}: ${ticketFetch.status}`);

          if (ticketFetch.ok) {
            const existingTicket = await ticketFetch.json();
            console.log(`📋 Existing ticket status: ${existingTicket.status} → syncing to: ${ticketStatus}`);

            const syncRes = await fetch(`${config.API_BASE_URL}/Tickets/${ticketRef}`, {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ ...existingTicket, status: ticketStatus }),
            });

            if (syncRes.ok) {
              console.log(`%c✅ Ticket synced! ${ticketRef} → ${ticketStatus}`,
                "color:#4bce97;font-weight:bold");
              // ✅ Show visual feedback
              Swal.fire({
                ...swalBase,
                icon: "success",
                title: "Status Synced!",
                html: `Card & Ticket updated to <strong style="color:#4bce97">${ticketStatus}</strong><br>
                                   <small style="color:#94a3b8">🎫 ${ticketRef}</small>`,
                timer: 2000,
                showConfirmButton: false,
              });
            } else {
              const errText = await syncRes.text();
              console.warn(`❌ Ticket sync failed: ${syncRes.status}`, errText);
            }
          } else {
            console.warn(`❌ Could not fetch ticket ${ticketRef}: ${ticketFetch.status}`);
          }
        } catch (e) {
          console.warn("Ticket sync error:", e);
        }
      } else {
        console.log(`ℹ️ No ticketRef on this card — skipping ticket sync`);
      }

    } catch (e) {
      console.error("updateCardStatus error:", e);
      await swalError("Failed to update status.");
    }
  };

  const deleteCard = async (id) => {
    if (!await swalConfirm("Delete card?", "This cannot be undone.", "Yes, delete")) return;
    try {
      const res = await fetch(`${config.API_BASE_URL}/cards/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error();
      setCards(p => p.filter(c => c.id !== id)); setOpenCard(null);
    } catch { await swalError("Failed to delete."); }
  };

  const archiveCard = async (id) => {
    const card = cards.find(c => c.id === id); if (!card) return;
    try {
      const res = await fetch(`${config.API_BASE_URL}/cards/${id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(toApiCard({ ...card, archived: true })) });
      if (!res.ok) throw new Error();
      setCards(p => p.map(c => c.id === id ? { ...c, archived: true } : c)); setOpenCard(null);
    } catch { await swalError("Failed to archive."); }
  };

  const handleDrop = (toColId, toStatus) => {
    const { cardId, fromCol, fromStatus } = window._dragCard || {};
    if (!cardId) return;
    const card = cards.find(c => c.id === cardId); if (!card) return;
    const colChanged = fromCol !== toColId;
    const statusChanged = toStatus && fromStatus !== toStatus;
    if (!colChanged && !statusChanged) { window._dragCard = null; return; }
    updateCard({ ...card, colId: colChanged ? toColId : card.colId, status: statusChanged ? toStatus : card.status });
    window._dragCard = null;
  };

  const renameCol = async (id, newTitle) => {
    const col = columns.find(c => c.id === id); if (!col) return;
    try {
      const res = await fetch(`${config.API_BASE_URL}/boards/${id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...col, title: newTitle }) });
      if (!res.ok) throw new Error();
      const saved = await res.json();
      setColumns(p => p.map(c => c.id === id ? { ...saved, color: resolveColor(saved.id, saved.color) } : c));
    } catch { await swalError("Failed to rename."); }
  };

  const handleColorChange = (id, newColor) => {
    setColumns(prev => prev.map(c => c.id === id ? { ...c, color: newColor } : c));
    const col = columns.find(c => c.id === id); if (!col) return;
    fetch(`${config.API_BASE_URL}/boards/${id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...col, color: newColor }) }).catch(() => { });
  };

  const deleteCol = async (id) => {
    const col = columns.find(c => c.id === id);
    const cnt = cards.filter(c => c.colId === id).length;
    if (!await swalConfirm(`Delete "${col?.title}"?`, cnt > 0 ? `Also deletes ${cnt} card(s).` : "Cannot be undone.", "Yes, delete")) return;
    try {
      const res = await fetch(`${config.API_BASE_URL}/boards/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error();
      setColumns(p => p.filter(c => c.id !== id)); setCards(p => p.filter(c => c.colId !== id));
    } catch { await swalError("Failed to delete."); }
  };

  return (
    <>
      <style>{`
        @keyframes bspin { to { transform: rotate(360deg); } }
        @keyframes ctspin { to { transform: rotate(360deg); } }
        .swb-popup { border: 1px solid #1e3a55 !important; border-radius: 16px !important; }
        .swb-input { background: #0f1e2e !important; border: 1px solid #1e3a55 !important; color: #dce8f5 !important; border-radius: 8px !important; font-family: inherit !important; }
        .swb-input:focus { border-color: #579dff !important; box-shadow: 0 0 0 3px rgba(87,157,255,.15) !important; }
        .board-scroll { scrollbar-width: thin; scrollbar-color: #1e3a55 transparent; }
        .board-scroll::-webkit-scrollbar { height: 7px; }
        .board-scroll::-webkit-scrollbar-thumb { background: #1e3a55; border-radius: 8px; }
        .board-scroll::-webkit-scrollbar-thumb:hover { background: #2d5a8a; }
      `}</style>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, ...bgStyle, transition: "background .3s" }}>
        {prefs.bgImage && <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.45)", pointerEvents: "none", zIndex: 0 }} />}

        {loading ? <Loader C={C} /> : (
          <div className="board-scroll" style={{ flex: 1, overflowX: "auto", overflowY: "hidden", display: "flex", gap: 16, padding: "24px 28px 80px", alignItems: "flex-start", minHeight: 0, position: "relative", zIndex: 1 }}>
            {columns.length === 0 && <EmptyBoard isSuperAdmin={superAdmin} C={C} />}
            {visibleColumns.map(col => (
              <Column key={col.id} col={col}
                cards={activeCards.filter(c => c.colId === col.id)}
                colWidth={colWidth}
                onAddCard={addCard} onCardClick={c => setOpenCard(c)} onDrop={handleDrop}
                onRename={renameCol} onDeleteCol={deleteCol} onStatusChange={updateCardStatus}
                onToggleTicketAllowed={toggleTicketAllowed}   // ← ADD
                onColorChange={handleColorChange} C={C}
              />
            ))}
            <AddColumnBtn onClick={addColumn} isSuperAdmin={superAdmin} colWidth={colWidth} C={C} />
          </div>
        )}

        {/* ── Footer Toolbar ── */}
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 500, padding: "8px 18px", display: "flex", alignItems: "center", gap: 10, background: `${C.colHeader}f0`, backdropFilter: "blur(14px)", borderTop: `1px solid ${C.border}`, minHeight: 52 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, flexShrink: 0 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>📋</span>
            <span style={{ fontSize: 11, color: C.textMuted, whiteSpace: "nowrap" }}>{columns.length} list{columns.length !== 1 ? "s" : ""}</span>
            <span style={{ fontSize: 11, color: C.textDim, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 7, padding: "2px 7px" }}>{THEMES[prefs.theme]?.icon} {THEMES[prefs.theme]?.name}</span>
            <span style={{ fontSize: 11, color: C.textDim, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 7, padding: "2px 7px" }}>{CARD_SIZES[prefs.cardSize]?.icon} {CARD_SIZES[prefs.cardSize]?.name}</span>
          </div>
          <div style={{ width: 1, height: 22, background: C.border, flexShrink: 0, marginLeft: 2 }} />
          <SearchFilterBar search={search} onSearch={setSearch} filter={filter} onFilter={setFilter} sort={sort} onSort={setSort} totalVisible={visibleColumns.length} totalAll={activeCards.length} C={C} />
          <BoardSettingsPanel theme={prefs.theme} cardSize={prefs.cardSize} bgOverride={prefs.bg || prefs.bgImage} C={C} onTheme={t => updatePref("theme", t)} onCardSize={s => updatePref("cardSize", s)} onBg={bg => updatePref("bg", bg)} onBgUpload={img => updatePref("bgImage", img)} />
        </div>
      </div>

      {openCard && (
        <IntegratedCardModal card={openCard} columns={columns} onClose={() => setOpenCard(null)}
          onUpdate={updateCard} onDelete={deleteCard} onArchive={archiveCard} C={C} />
      )}

      {showAssignModal && (
        <AssignBoardModal admins={admins} currentUser={currentUser}
          onConfirm={handleAssignConfirm} onClose={() => { setShowAssignModal(false); setPendingColData(null); }} C={C} />
      )}
    </>
  );
}